// src/app/components/admin/health-details-section.tsx
//
// Global Settings → Health Details (UPM-169). Read-only answers to "what is
// running in production right now?" without asking a person:
//   * the build (commit SHA + build time) the backend and this frontend bundle
//     were built from, and whether they agree
//   * live checks of the database, Redis and the celery workers
//   * every process that logged in the last hour and the build it reported —
//     the only place the celery workers' build is visible
//   * log volume by level over the last 24 hours
import { useCallback, useEffect, useState, type CSSProperties, type ReactNode } from "react";
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  GitCommit,
  Loader2,
  RefreshCcw,
  Server,
} from "lucide-react";

import { Button } from "../ui/button";
import {
  frontendBuild,
  opsApi,
  type BuildInfo,
  type HealthCheck,
  type HealthDetails,
} from "@/api/ops";

const CHECK_LABELS: Record<string, string> = {
  database: "Database (PostgreSQL)",
  redis: "Redis (broker + progress)",
  celery_workers: "Celery workers",
};

const LEVEL_ORDER = ["CRITICAL", "ERROR", "WARNING", "INFO"];

export function levelColor(level: string): string {
  switch (level.toUpperCase()) {
    case "CRITICAL":
    case "ERROR":
      return "var(--infrared-coral)";
    case "WARNING":
      return "var(--impact-medium)";
    default:
      return "var(--signal-blue)";
  }
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d) return `${d}d ${h}h`;
  if (h) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatBytes(bytes: unknown): string | null {
  if (typeof bytes !== "number") return null;
  const units = ["B", "KB", "MB", "GB", "TB"];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

const cardStyle: CSSProperties = {
  backgroundColor: "var(--card)",
  border: "1px solid var(--border)",
  borderRadius: 12,
  padding: 20,
};

const monoStyle: CSSProperties = {
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
  fontSize: 12.5,
};

function Card({ title, icon, right, children }: { title: string; icon: ReactNode; right?: ReactNode; children: ReactNode }) {
  return (
    <section style={cardStyle}>
      <div className="flex items-center justify-between gap-3 flex-wrap" style={{ marginBottom: 14 }}>
        <div className="flex items-center gap-2" style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}>
          {icon}
          {title}
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

function StatusPill({ ok, label }: { ok: boolean; label: string }) {
  const color = ok ? "var(--signal-green)" : "var(--infrared-coral)";
  return (
    <span
      className="inline-flex items-center gap-1"
      style={{
        padding: "2px 9px",
        borderRadius: 999,
        fontSize: 11.5,
        fontWeight: 600,
        color,
        border: `1px solid ${color}`,
        whiteSpace: "nowrap",
      }}
    >
      {ok ? <CheckCircle2 style={{ width: 12, height: 12 }} /> : <AlertCircle style={{ width: 12, height: 12 }} />}
      {label}
    </span>
  );
}

function KeyValue({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 11.5, color: "var(--muted-foreground)", marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, color: "var(--foreground)", overflowWrap: "anywhere" }}>{children}</div>
    </div>
  );
}

function BuildBlock({ title, build }: { title: string; build: BuildInfo }) {
  return (
    <div style={{ padding: 14, borderRadius: 10, border: "1px solid var(--border)", minWidth: 0 }}>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>{title}</div>
      <div className="grid gap-2" style={{ gridTemplateColumns: "1fr" }}>
        <KeyValue label="Commit">
          <span style={monoStyle}>{build.sha ?? "not a deployed build"}</span>
        </KeyValue>
        <KeyValue label="Built at">{formatDateTime(build.built_at)}</KeyValue>
      </div>
    </div>
  );
}

function checkDetail(check: HealthCheck): string {
  const d = check.detail || {};
  if (d.error) return String(d.error);
  if (check.name === "database") {
    return [d.server_version ? `PostgreSQL ${d.server_version}` : null, formatBytes(d.size_bytes)]
      .filter(Boolean)
      .join(" · ");
  }
  if (check.name === "celery_workers" && Array.isArray(d.workers)) {
    return `${d.workers.length} answered: ${d.workers.join(", ")}`;
  }
  return "";
}

export function HealthDetailsSection() {
  const [data, setData] = useState<HealthDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setErr("");
    setLoading(true);
    try {
      setData(await opsApi.health());
    } catch (e: any) {
      setErr(e?.message || "Failed to load health details");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const backendSha = data?.build.sha ?? null;
  const buildsDisagree = !!(backendSha && frontendBuild.sha && backendSha !== frontendBuild.sha);

  // last_24h: [{source, level, count}] -> {level: total}, {source: {level: n}}
  const totals: Record<string, number> = {};
  const bySource: Record<string, Record<string, number>> = {};
  (data?.logs.last_24h ?? []).forEach(({ source, level, count }) => {
    totals[level] = (totals[level] ?? 0) + count;
    bySource[source] = { ...(bySource[source] ?? {}), [level]: count };
  });

  return (
    <div className="px-4 md:px-6 py-5 md:py-6">
      {/* ── Header ──────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Activity style={{ width: 22, height: 22, color: "var(--signal-green)" }} />
            <h2
              style={{
                fontFamily: "var(--font-sans)",
                fontSize: "clamp(var(--text-xl), 4.5vw, var(--text-2xl))",
                fontWeight: 700,
                margin: 0,
                lineHeight: 1.3,
              }}
            >
              Health Details
            </h2>
            {data && <StatusPill ok={data.status === "ok"} label={data.status === "ok" ? "All checks passing" : "Degraded"} />}
          </div>
          <div style={{ marginTop: 6, fontSize: 12, color: "var(--muted-foreground)" }}>
            What this environment is running and whether its dependencies answer.
            {data && <> Checked {formatDateTime(data.checked_at)}.</>}
          </div>
        </div>
        <Button
          onClick={load}
          disabled={loading}
          variant="secondary"
          className="w-full sm:w-auto flex-shrink-0"
          style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "center" }}
        >
          {loading ? <Loader2 className="animate-spin" style={{ width: 16, height: 16 }} /> : <RefreshCcw style={{ width: 16, height: 16 }} />}
          Refresh
        </Button>
      </div>

      {err && (
        <div
          className="mt-4 p-3 flex items-start gap-2 rounded-lg"
          style={{ backgroundColor: "var(--infrared-coral-5)", border: "1px solid var(--infrared-coral)", fontSize: 13 }}
        >
          <AlertCircle style={{ width: 16, height: 16, color: "var(--infrared-coral)", flexShrink: 0, marginTop: 1 }} />
          {err}
        </div>
      )}

      {!data && loading && (
        <div className="mt-6 flex items-center gap-2" style={{ fontSize: 13, color: "var(--muted-foreground)" }}>
          <Loader2 className="animate-spin" style={{ width: 16, height: 16 }} /> Running checks…
        </div>
      )}

      {data && (
        <div className="mt-5 grid gap-4">
          {/* ── Build ──────────────────────────────────────────────── */}
          <Card title="Build" icon={<GitCommit style={{ width: 16, height: 16, color: "var(--alpha-teal)" }} />}>
            <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
              <BuildBlock title="Backend (this API process)" build={data.build} />
              <BuildBlock title="Frontend (this browser's bundle)" build={frontendBuild} />
            </div>
            {buildsDisagree && (
              <div
                className="mt-3 p-3 flex items-start gap-2 rounded-lg"
                style={{ border: "1px solid var(--impact-medium)", fontSize: 12.5 }}
              >
                <AlertTriangle style={{ width: 15, height: 15, color: "var(--impact-medium)", flexShrink: 0, marginTop: 1 }} />
                The frontend and backend were built from different commits. A rollout may still be in
                progress, or this browser has an old bundle cached — reload the page and check again.
              </div>
            )}
            <div className="mt-4 grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))" }}>
              <KeyValue label="Environment">{data.process.environment}</KeyValue>
              <KeyValue label="Host">
                <span style={monoStyle}>{data.process.host}</span>
              </KeyValue>
              <KeyValue label="Up for">{formatUptime(data.process.uptime_s)}</KeyValue>
              <KeyValue label="Python">{data.process.python}</KeyValue>
            </div>
          </Card>

          {/* ── Dependencies ───────────────────────────────────────── */}
          <Card title="Dependencies" icon={<Server style={{ width: 16, height: 16, color: "var(--signal-blue)" }} />}>
            <div className="grid gap-2">
              {data.checks.map((check) => (
                <div
                  key={check.name}
                  className="flex items-start justify-between gap-3 flex-wrap"
                  style={{ padding: "10px 12px", borderRadius: 9, border: "1px solid var(--border)" }}
                >
                  <div style={{ minWidth: 0, flex: "1 1 220px" }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{CHECK_LABELS[check.name] ?? check.name}</div>
                    {checkDetail(check) && (
                      <div style={{ fontSize: 12, color: "var(--muted-foreground)", marginTop: 2, overflowWrap: "anywhere" }}>
                        {checkDetail(check)}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {check.latency_ms !== null && (
                      <span style={{ fontSize: 12, color: "var(--muted-foreground)" }}>{check.latency_ms} ms</span>
                    )}
                    <StatusPill ok={check.status === "ok"} label={check.status === "ok" ? "OK" : "Failing"} />
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* ── Processes ──────────────────────────────────────────── */}
          <Card title="Processes reporting (last hour)" icon={<Activity style={{ width: 16, height: 16, color: "var(--spectral-violet)" }} />}>
            {data.processes === null ? (
              <EmptyNote>
                Not available until the log table exists (apply <code>2026-10-22_app_logs.sql</code>).
              </EmptyNote>
            ) : data.processes.length === 0 ? (
              <EmptyNote>No process has written a log line in the last hour.</EmptyNote>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                  <thead>
                    <tr style={{ color: "var(--muted-foreground)", textAlign: "left" }}>
                      <th style={thStyle}>Source</th>
                      <th style={thStyle}>Host</th>
                      <th style={thStyle}>Commit</th>
                      <th style={thStyle}>Processes</th>
                      <th style={thStyle}>Last seen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.processes.map((p) => {
                      const differs = !!(backendSha && p.sha && p.sha !== backendSha);
                      return (
                        <tr key={`${p.source}-${p.host}-${p.sha}`} style={{ borderTop: "1px solid var(--border)" }}>
                          <td style={tdStyle}>{p.source}</td>
                          <td style={{ ...tdStyle, ...monoStyle }}>{p.host ?? "—"}</td>
                          <td style={{ ...tdStyle, ...monoStyle, color: differs ? "var(--impact-medium)" : undefined }}>
                            {p.sha ?? "—"}
                            {differs && " (differs)"}
                          </td>
                          <td style={tdStyle}>{p.pids}</td>
                          <td style={tdStyle}>{formatDateTime(p.last_seen)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* ── Logs ───────────────────────────────────────────────── */}
          <Card title="Logs (last 24 hours)" icon={<AlertCircle style={{ width: 16, height: 16, color: "var(--infrared-coral)" }} />}>
            {!data.logs.table_present ? (
              <EmptyNote>
                Log capture has nowhere to write yet — apply <code>2026-10-22_app_logs.sql</code>.
              </EmptyNote>
            ) : (
              <>
                <div className="flex flex-wrap gap-3">
                  {LEVEL_ORDER.filter((l) => l !== "CRITICAL" || totals[l]).map((level) => (
                    <div key={level} style={{ padding: "10px 14px", borderRadius: 9, border: "1px solid var(--border)", minWidth: 110 }}>
                      <div style={{ fontSize: 11.5, color: levelColor(level), fontWeight: 600 }}>{level}</div>
                      <div style={{ fontSize: 20, fontWeight: 700 }}>{(totals[level] ?? 0).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
                {Object.keys(bySource).length > 1 && (
                  <div className="mt-3" style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
                    {Object.entries(bySource).map(([source, levels]) => (
                      <div key={source}>
                        {source}: {(levels.ERROR ?? 0) + (levels.CRITICAL ?? 0)} errors, {levels.WARNING ?? 0} warnings, {levels.INFO ?? 0} info
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-3" style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
                  Kept for {data.logs.retention_days} days.
                  {!data.logs.enabled_in_this_process && " Capture is off in this API process (not a deployed build, or APP_LOG_CAPTURE=0)."}{" "}
                  Read them in the Logs section.
                </div>
              </>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}

const thStyle: CSSProperties = { padding: "6px 8px", fontWeight: 500, whiteSpace: "nowrap" };
const tdStyle: CSSProperties = { padding: "8px", verticalAlign: "top" };

function EmptyNote({ children }: { children: ReactNode }) {
  return <div style={{ fontSize: 12.5, color: "var(--muted-foreground)", lineHeight: 1.6 }}>{children}</div>;
}

export default HealthDetailsSection;
